import { Flex } from 'antd';
import React, { useState } from 'react';
import {
  Button,
  IconType,
  semanticTheme,
  TextArea,
  Typography
} from '@tools/ui/dime';
import useT from '@tools/ui-hooks/useT';

export interface FeedbackFormProps {
  onSubmit: (helpful: boolean, message?: string) => Promise<boolean> | boolean;
  prompt?: string;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({ onSubmit, prompt }) => {
  const { t } = useT();
  const [selectedFeedback, setSelectedFeedback] = useState<boolean | null>(
    null
  );
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isNegative = selectedFeedback === false;
  const canSubmit = !isNegative || !!feedbackMessage.trim();

  const handleThumbClick = (helpful: boolean) => {
    setSelectedFeedback(helpful);
    setFeedbackMessage('');
  };

  const handleSubmit = async () => {
    if (selectedFeedback === null) return;
    setIsSubmitting(true);
    try {
      const success = await onSubmit(
        selectedFeedback,
        feedbackMessage.trim() || undefined
      );
      if (success) {
        setSelectedFeedback(null);
        setFeedbackMessage('');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedFeedback(null);
    setFeedbackMessage('');
  };

  return (
    <Flex vertical gap={semanticTheme.dime.spacing.m}>
      <Flex align="center" justify="space-between">
        {!!prompt && (
          <Typography
            variant="body1"
            color={semanticTheme.dime.colors.text.secondary}
          >
            {prompt}
          </Typography>
        )}
        <Flex gap={semanticTheme.dime.spacing.s}>
          <Button
            variant={selectedFeedback === true ? 'primary' : 'secondary'}
            iconOnly={IconType.ThumbUp}
            onClick={() => handleThumbClick(true)}
            disabled={isSubmitting}
          />
          <Button
            variant={selectedFeedback === false ? 'primary' : 'secondary'}
            iconOnly={IconType.ThumbDown}
            onClick={() => handleThumbClick(false)}
            disabled={isSubmitting}
          />
        </Flex>
      </Flex>
      {selectedFeedback !== null && (
        <>
          <TextArea
            placeholder={
              isNegative
                ? t('vendor.summary.feedbackNegativePlaceholder')
                : t('vendor.summary.feedbackPositivePlaceholder')
            }
            value={feedbackMessage}
            onChange={(e) => setFeedbackMessage(e.target.value)}
            rows={3}
            disabled={isSubmitting}
          />
          <Flex gap={semanticTheme.dime.spacing.s} justify="flex-end">
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              {t('vendor.summary.feedbackCancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={!canSubmit}
            >
              {t('vendor.summary.feedbackSubmit')}
            </Button>
          </Flex>
        </>
      )}
    </Flex>
  );
};

export default FeedbackForm;
